import { extractPaginationState } from "api/mappers";
import type { PaginatedResult, PaginationState } from "api/types";
import type { TeacherScheduledTask } from "portfolio/types";
import { defineStore } from "pinia";
import { selectedFiltersToQueryParams } from "shared/mappers/filter.mapper";
import type { Filter, HasFetchedState, SelectedFilters } from "shared/types";
import { resetHasFetched, shouldFetch } from "shared/utils";
import { ref } from "vue";

/**
 * Fetchers backing a classroom "Taken" tab. `context` is the class code the
 * tab belongs to: a schedule id for a lesson group, the class name for a
 * mentor class.
 */
export type ClassroomTaskListFetchers = {
  fetchList: (
    params: Record<string, string>,
    context: string,
  ) => Promise<PaginatedResult<TeacherScheduledTask>>;
  fetchFilters: (context: string) => Promise<Filter[]>;
};

/** The class must be set before fetching, or the URL would be malformed. */
const requireContext = (context: string | null): string => {
  if (context === null || context === "") {
    throw new Error(
      "Classroom task store used without a class context; call setContext first.",
    );
  }

  return context;
};

/**
 * Builds a Pinia store for one classroom "Taken" tab. The mentor-class and
 * lesson-group tabs share this body; they differ only in the endpoints their
 * fetchers hit.
 */
export const createClassroomTaskListStore = (
  storeId: string,
  fetchers: ClassroomTaskListFetchers,
) =>
  defineStore(storeId, () => {
    const tasks = ref<TeacherScheduledTask[]>([]);
    const tasksPagination = ref<PaginationState | null>(null);
    const taskFilters = ref<Filter[]>([]);
    const context = ref<string | null>(null);

    const isFetching = ref<false | Promise<void>>(false);
    const hasFetched = ref<HasFetchedState>({ tasks: false, filters: false });

    // Only reuse an in-flight request when the filters match, so a stale
    // response from a superseded filter set cannot overwrite fresher state.
    let tasksKey: string | null = null;

    /** The in-flight filters request, so a second mount joins it. */
    let filtersRequest: Promise<Filter[]> | null = null;

    const $reset = (): void => {
      tasks.value = [];
      tasksPagination.value = null;
      taskFilters.value = [];
      tasksKey = null;
      filtersRequest = null;
      resetHasFetched(hasFetched.value);
    };

    const setContext = (next: string | null): void => {
      if (next === context.value) {
        return;
      }

      context.value = next;
      $reset();
    };

    const loadTasks = async (filters: SelectedFilters = {}): Promise<void> => {
      const params = selectedFiltersToQueryParams(filters);
      const key = JSON.stringify([context.value, params]);

      if (isFetching.value !== false && tasksKey === key) {
        return isFetching.value;
      }

      tasksKey = key;

      isFetching.value = (async () => {
        try {
          const result = await fetchers.fetchList(
            params,
            requireContext(context.value),
          );

          if (tasksKey !== key) {
            return;
          }

          tasks.value = result.items;
          tasksPagination.value = extractPaginationState(result);
          hasFetched.value.tasks = new Date();
        } finally {
          if (tasksKey === key) {
            isFetching.value = false;
          }
        }
      })();

      return isFetching.value;
    };

    // The options behind these filters are the school's whole roster, so a
    // refetch is expensive. FilterPanel calls this from every `onMounted`,
    // which is once per visit to the tab; `setContext` resets the map, so
    // switching class still fetches.
    const loadTaskFilters = async ({
      forceFetch = false,
    }: { forceFetch?: boolean } = {}): Promise<Filter[]> => {
      if (filtersRequest !== null) {
        return filtersRequest;
      }

      if (
        forceFetch === false &&
        shouldFetch(hasFetched.value.filters) === false
      ) {
        return taskFilters.value;
      }

      const contextKey = context.value;

      filtersRequest = (async () => {
        try {
          const filters = await fetchers.fetchFilters(
            requireContext(contextKey),
          );

          // `setContext` reset us mid-flight: these options belong to a class
          // the teacher already left.
          if (contextKey !== context.value) {
            return taskFilters.value;
          }

          taskFilters.value = filters;
          hasFetched.value.filters = new Date();

          return filters;
        } finally {
          filtersRequest = null;
        }
      })();

      return filtersRequest;
    };

    return {
      tasks,
      tasksPagination,
      taskFilters,
      context,
      isFetching,
      hasFetched,
      setContext,
      loadTasks,
      loadTaskFilters,
      $reset,
    };
  });
